import type { ReactNode } from 'react';
import { usePermissions } from '@helios/auth-firebase-web';
import type { AccessLevel } from '@helios/authz';

interface PermissionGateProps {
  module: string;
  subModule: string;
  /** Minimum access required to render children. Defaults to 'view'. */
  minimumAccess?: AccessLevel;
  /** Rendered when access is denied (default: nothing). */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * PRD.268 — page action gating. Hides (or replaces with `fallback`) its
 * children unless the current user has at least `minimumAccess` on the given
 * module/sub-module. Use to hide create/edit/save/delete controls for
 * view-only users.
 *
 * Example:
 *   <PermissionGate module={MODULES.INVENTORY} subModule="Inventory Items" minimumAccess="edit">
 *     <SaveButton />
 *   </PermissionGate>
 */
export function PermissionGate({
  module,
  subModule,
  minimumAccess = 'view',
  fallback = null,
  children,
}: PermissionGateProps) {
  const { canAccess } = usePermissions();
  return canAccess({ module, subModule, minimumAccess }) ? <>{children}</> : <>{fallback}</>;
}
